import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  MeasurementTypes,
  SelectMeasurementPayloadType,
} from "../../../../../../@types/Measurements";
import { SkeletonLoading } from "../../../../../../components/Skeleton";
import { Tag } from "../../../../../../components/Tag";
import { getSpirometryGradeTagVariant } from "../../../../../../constants/spirometryConstants";
import { useArtiQSpirometryData } from "../../../../../../hooks/queries/spirometry";
import { isProduction, isUat } from "../../../../../../utils/environment";
import { BlowAcceptabilitySection } from "../../../MeasurementModal/components/BlowAcceptabilitySection";
import {
  ArtiQContainer,
  SubtitleGradeContainer,
} from "../../../MeasurementModal/components/SpirometrySection/SpirometrySection.styles";
import { SpirometrySectionChart } from "../../../MeasurementModal/components/SpirometrySection/SpirometrySectionChart";
import { SpirometryValuesSection } from "../../../MeasurementModal/components/SpirometryValuesSection";
import {
  Title,
  Subtitle,
} from "../../../MeasurementModal/MeasurementModal.styles";
import { SessionGraphContainer } from "./SessionDetailsMobile.styles";

type SessionDetailsMobileProps = {
  selectedMeasurement: SelectMeasurementPayloadType;
  measurementType: MeasurementTypes;
};

export function SessionDetailsMobile({
  selectedMeasurement,
  measurementType,
}: SessionDetailsMobileProps) {
  const { t } = useTranslation();

  const [linesToShow, setLinesToShow] = useState([
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
  ]);

  useEffect(() => {
    setLinesToShow([true, true, true, true, true, true, true, true]);
  }, [selectedMeasurement]);

  const { artiQData, isFetching } = useArtiQSpirometryData(
    selectedMeasurement?.spirometryTestIds || []
  );

  const fvcGrade = artiQData?.fvcGrade;
  const fev1Grade = artiQData?.fev1Grade;
  const volumeFlowValues = artiQData?.flowVolumeAxes;
  const timeVolumeValues = artiQData?.timeVolumeAxes;

  const fvcGradeVariant = getSpirometryGradeTagVariant(fvcGrade);
  const fev1GradeVariant = getSpirometryGradeTagVariant(fev1Grade);

  // EN-5002: session grades are not ready for production/UAT rollout — only
  // show them on dev/staging until product green-lights them.
  const showSessionGrades = !isProduction() && !isUat();

  return (
    <>
      <Title>{t("patientPage_mobile_selectedSession")}</Title>
      <SubtitleGradeContainer>
        <Subtitle css={{ marginTop: 0, marginBottom: 0 }}>
          {selectedMeasurement.time}
        </Subtitle>
      </SubtitleGradeContainer>

      <SubtitleGradeContainer>
        {showSessionGrades && fvcGrade && fvcGradeVariant && (
          <Tag variant={fvcGradeVariant}>FVC = {fvcGrade}</Tag>
        )}

        {showSessionGrades && fev1Grade && fev1GradeVariant && (
          <Tag variant={fev1GradeVariant}>FEV1 = {fev1Grade}</Tag>
        )}
      </SubtitleGradeContainer>

      <SubtitleGradeContainer>
        {artiQData?.percentPredictedWarning && !isFetching ? (
          <Tag variant="error">{artiQData.percentPredictedWarning}</Tag>
        ) : null}
      </SubtitleGradeContainer>

      <SessionGraphContainer>
        {volumeFlowValues ? (
          <SpirometrySectionChart
            xConfig={{
              dataKey: "x",
              type: "number",
              minTickGap: 2,
              allowDecimals: false,
              tickFormatter(value) {
                return Number.parseFloat(value).toFixed(1);
              },
            }}
            yConfig={{
              axisSize: 42,
              domain: [0, (dataMax: any) => Math.ceil(dataMax)],
            }}
            chartConfig={[
              {
                yKey: "y",
                dot: false,
                color: "#7562A8",
                lineType: "monotone",
              },
            ]}
            width="100%"
            height={207}
            data={volumeFlowValues ?? []}
            linesToShow={linesToShow}
          />
        ) : (
          <div style={{ width: "90%", marginLeft: "30px" }}>
            <SkeletonLoading height="200px" width="100%" borderRadius="0px" />
          </div>
        )}
      </SessionGraphContainer>

      <SessionGraphContainer>
        {timeVolumeValues ? (
          <SpirometrySectionChart
            xConfig={{
              dataKey: "x",
              type: "number",
              minTickGap: 2,
              allowDecimals: false,
              tickFormatter(value) {
                return Number.parseFloat(value).toFixed(1);
              },
            }}
            yConfig={{
              axisSize: 42,
            }}
            chartConfig={[
              {
                yKey: "y",
                dot: false,
                color: "#7562A8",
                lineType: "monotone",
              },
            ]}
            width="100%"
            height={207}
            data={timeVolumeValues ?? []}
            linesToShow={linesToShow}
          />
        ) : (
          <div style={{ width: "90%", marginLeft: "30px" }}>
            <SkeletonLoading height="200px" width="100%" borderRadius="0px" />
          </div>
        )}
      </SessionGraphContainer>

      <ArtiQContainer>
        <SpirometryValuesSection
          spirometryTests={artiQData?.spirometryTests}
          archivedSpirometryTestsIds={[]}
        />
        <BlowAcceptabilitySection
          measurementType={measurementType}
          archivedSpirometryTestsIds={[]}
          blows={artiQData?.blowAcceptability}
        />
      </ArtiQContainer>
    </>
  );
}
